import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  ChevronLeft, 
  ChevronRight, 
  ZoomIn, 
  ZoomOut, 
  Download, 
  FileText, 
  Loader2, 
  AlertCircle, 
  MessageSquare,
  Sparkles,
  Shield,
  Zap,
  ArrowRight,
  Eye,
  Search,
  FolderOpen,
  Share2,
  ScanLine,
  CheckCircle2
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import CommentsSection from "@/components/shared/CommentsSection";
import { cn } from "@/lib/utils";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import easyfinderLogo from "@/assets/easyfinder-logo-google.png";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1 }
};

const slideInFromRight = {
  hidden: { opacity: 0, x: 30 },
  visible: { opacity: 1, x: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};

const featureItem = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0 }
};

// PDF.js worker configured in src/lib/pdfConfig.ts

interface ShareData {
  id: string;
  token: string;
  permission: string;
  expires_at: string | null;
  view_count: number;
  document: {
    id: string;
    title: string;
    file_name: string;
    file_path: string;
    file_size: number | null;
    mime_type: string | null;
    created_at: string;
    doc_type: string;
  };
}

const TYPE_LABELS: Record<string, string> = {
  invoice: "Facture",
  contract: "Contrat",
  tax: "Document fiscal",
  insurance: "Assurance",
  receipt: "Reçu",
  quote: "Devis",
  identity: "Identité",
  payslip: "Bulletin de salaire",
  attestation: "Attestation",
  bank_statement: "Relevé bancaire",
  medical_certificate: "Certificat médical",
  notarial_deed: "Acte notarié",
  administrative: "Administratif",
  warranty: "Garantie",
  other: "Document",
};

const TYPE_EMOJIS: Record<string, string> = {
  invoice: "🧾",
  contract: "📝",
  tax: "📊",
  insurance: "🛡️",
  receipt: "🧾",
  quote: "💰",
  identity: "🪪",
  payslip: "💵",
  attestation: "📋",
  bank_statement: "🏦",
  medical_certificate: "🏥",
  notarial_deed: "⚖️",
  administrative: "📁",
  warranty: "🔧",
  other: "📄",
};

const SharedDocument = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [shareData, setShareData] = useState<ShareData | null>(null);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1);
  const [showComments, setShowComments] = useState(true);

  useEffect(() => {
    if (!token) {
      setError("Lien invalide");
      setLoading(false);
      return;
    }

    const fetchShareData = async () => {
      try {
        const { data, error: fetchError } = await supabase.functions.invoke("get-shared-document", {
          body: { token }
        });

        if (fetchError) throw fetchError;

        if (!data || data.error) {
          setError(data?.error || "Ce lien de partage n'existe pas ou a été supprimé.");
          setLoading(false);
          return;
        }

        const transformedData: ShareData = {
          id: data.shareLink.id,
          token: token,
          permission: data.shareLink.permission,
          expires_at: data.shareLink.expires_at,
          view_count: data.shareLink.view_count,
          document: {
            id: data.document.id,
            title: data.document.title,
            file_name: data.document.file_name,
            file_path: data.document.file_path,
            file_size: data.document.file_size,
            mime_type: data.document.mime_type,
            created_at: data.document.created_at,
            doc_type: data.document.doc_type,
          },
        };

        setShareData(transformedData);

        if (data.fileBase64) {
          const binaryString = atob(data.fileBase64);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          const blob = new Blob([bytes], { type: data.mimeType || "application/octet-stream" });
          const url = URL.createObjectURL(blob);
          setFileUrl(url);
        }
      } catch (err: any) {
        console.error("Error:", err);
        setError("Une erreur est survenue.");
      } finally {
        setLoading(false);
      }
    };

    fetchShareData();

    return () => {
      if (fileUrl) {
        URL.revokeObjectURL(fileUrl);
      }
    };
  }, [token]);

  const isPdf = shareData?.document.mime_type === "application/pdf";
  const isImage = shareData?.document.mime_type?.startsWith("image/");
  const canComment = shareData?.permission === "read_comment";

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const handleDownload = async () => {
    if (!shareData || !fileUrl) return;

    const a = window.document.createElement("a");
    a.href = fileUrl;
    a.download = shareData.document.file_name;
    window.document.body.appendChild(a);
    a.click();
    window.document.body.removeChild(a);
  };

  // Logo Component
  const EasyFinderLogo = ({ size = "default" }: { size?: "small" | "default" | "large" }) => {
    const sizes = {
      small: { img: "w-8 h-8", text: "text-base" },
      default: { img: "w-10 h-10", text: "text-xl" },
      large: { img: "w-14 h-14", text: "text-2xl" },
    };
    const s = sizes[size];
    
    return (
      <div className="flex items-center gap-2.5">
        <img 
          src={easyfinderLogo} 
          alt="EasyFinder" 
          className={cn(s.img, "rounded-xl shadow-lg shadow-primary/25")} 
        />
        <span className={cn(s.text, "font-bold bg-gradient-to-r from-primary to-indigo-600 bg-clip-text text-transparent")}>
          EasyFinder
        </span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-6">
          <EasyFinderLogo size="large" />
          <div className="flex flex-col items-center gap-2">
            <div className="relative">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
                <FileText className="w-8 h-8 text-primary" />
              </div>
              <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                <Loader2 className="w-3.5 h-3.5 text-white animate-spin" />
              </div>
            </div>
            <p className="text-sm text-muted-foreground mt-2">Chargement du document...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-red-50/30 flex flex-col">
        {/* Header with Logo */}
        <header className="border-b border-border/40 bg-white/80 backdrop-blur-xl">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4">
            <button onClick={() => navigate("/")} className="hover:opacity-80 transition-opacity">
              <EasyFinderLogo />
            </button>
          </div>
        </header>

        <div className="flex-1 flex items-center justify-center p-4">
          <div className="max-w-md w-full text-center">
            <div className="w-20 h-20 rounded-3xl bg-destructive/10 flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-10 h-10 text-destructive" />
            </div>
            <h1 className="text-2xl font-bold mb-3">Document inaccessible</h1>
            <p className="text-muted-foreground mb-8">{error}</p>
            <Button onClick={() => navigate("/")} size="lg" className="rounded-xl">
              Découvrir EasyFinder
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const docType = shareData?.document.doc_type || 'other';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 flex flex-col">
      {/* Premium Header with EasyFinder Branding */}
      <motion.header 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="sticky top-0 z-50 backdrop-blur-2xl bg-white/90 border-b border-border/40 shadow-sm"
      >
        <div className="px-4 sm:px-6 py-3">
          <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
            {/* Logo & Brand */}
            <motion.button 
              onClick={() => navigate("/")} 
              className="hover:opacity-80 transition-opacity shrink-0"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <EasyFinderLogo size="small" />
            </motion.button>

            {/* Document Info - Center */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="flex items-center gap-3 min-w-0 flex-1 justify-center"
            >
              <div className="hidden sm:flex w-11 h-11 rounded-xl bg-gradient-to-br from-primary/10 to-indigo-100 items-center justify-center shrink-0">
                <span className="text-lg">{TYPE_EMOJIS[docType]}</span>
              </div>
              <div className="min-w-0 text-center sm:text-left">
                <h1 className="font-semibold text-sm sm:text-base leading-tight line-clamp-1">
                  {shareData?.document.title}
                </h1>
                <div className="flex items-center justify-center sm:justify-start gap-2 mt-0.5">
                  <Badge variant="secondary" className="text-[10px] font-medium bg-primary/10 text-primary border-0">
                    {TYPE_LABELS[docType]}
                  </Badge>
                  <span className="text-[10px] sm:text-xs text-muted-foreground flex items-center gap-1">
                    <Eye className="w-3 h-3" />
                    {shareData?.view_count}
                  </span>
                </div>
              </div>
            </motion.div>
            
            {/* Actions */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
              className="flex items-center gap-2 shrink-0"
            >
              {canComment && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowComments(!showComments)}
                  className={cn(
                    "h-9 gap-2 rounded-lg",
                    showComments && "bg-primary/10 text-primary"
                  )}
                >
                  <MessageSquare className="w-4 h-4" />
                  <span className="hidden sm:inline">Discussion</span>
                </Button>
              )}
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleDownload}
                className="h-9 gap-2 rounded-lg border-primary/20 hover:bg-primary/5 hover:border-primary/40"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">Télécharger</span>
              </Button>
            </motion.div>
          </div>
        </div>
      </motion.header>

      {/* "Shared via EasyFinder" Badge */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="bg-gradient-to-r from-primary/5 via-indigo-50/50 to-primary/5 border-b border-primary/10"
      >
        <div className="max-w-6xl mx-auto px-4 py-2 flex items-center justify-center gap-2">
          <Shield className="w-3.5 h-3.5 text-primary" />
          <span className="text-xs text-primary/80 font-medium">
            Document partagé en toute sécurité via EasyFinder
          </span>
        </div>
      </motion.div>

      {/* PDF Toolbar */}
      {isPdf && (
        <div className="border-b border-border/30 bg-white/60 backdrop-blur-sm">
          <div className="max-w-6xl mx-auto flex items-center justify-between px-4 py-2">
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-lg"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm min-w-[70px] text-center font-medium tabular-nums">
                {currentPage} / {numPages}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-lg"
                onClick={() => setCurrentPage((p) => Math.min(numPages, p + 1))}
                disabled={currentPage >= numPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-lg"
                onClick={() => setScale((s) => Math.max(0.5, s - 0.25))}
                disabled={scale <= 0.5}
              >
                <ZoomOut className="w-4 h-4" />
              </Button>
              <span className="text-sm min-w-[50px] text-center font-medium tabular-nums">
                {Math.round(scale * 100)}%
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-lg"
                onClick={() => setScale((s) => Math.min(2, s + 0.25))}
                disabled={scale >= 2}
              >
                <ZoomIn className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Document Preview */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="flex-1 overflow-auto bg-gradient-to-b from-transparent to-slate-50/50"
      >
        <div className="min-h-full flex items-start justify-center p-4 sm:p-6">
          {isPdf && fileUrl ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.4 }}
            >
              <Document
                file={fileUrl}
                onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                loading={
                  <div className="flex items-center gap-3 py-20">
                    <Loader2 className="w-5 h-5 animate-spin text-primary" />
                    <span className="text-muted-foreground">Chargement du PDF...</span>
                  </div>
                }
                className="w-full flex justify-center"
              >
                <Page
                  pageNumber={currentPage}
                  scale={scale}
                  className="shadow-2xl rounded-lg overflow-hidden ring-1 ring-border/50"
                  renderTextLayer={true}
                  renderAnnotationLayer={true}
                  width={typeof window !== 'undefined' ? Math.min(window.innerWidth - 32, 800) * scale : undefined}
                />
              </Document>
            </motion.div>
          ) : isImage && fileUrl ? (
            <motion.img
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.4 }}
              src={fileUrl}
              alt={shareData?.document.title}
              className="max-w-full max-h-[calc(100vh-300px)] object-contain rounded-xl shadow-2xl ring-1 ring-border/50"
              style={{ transform: `scale(${scale})`, transformOrigin: "center" }}
            />
          ) : (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.4 }}
              className="flex flex-col items-center justify-center py-20 gap-4"
            >
              <div className="w-20 h-20 rounded-2xl bg-muted/50 flex items-center justify-center">
                <FileText className="w-10 h-10 text-muted-foreground/50" />
              </div>
              <p className="text-muted-foreground font-medium">
                Prévisualisation non disponible
              </p>
              <Button onClick={handleDownload} className="gap-2 rounded-xl">
                <Download className="w-4 h-4" />
                Télécharger le fichier
              </Button>
            </motion.div>
          )}
        </div>
      </motion.div>

      {/* Comments Section */}
      {shareData && canComment && showComments && (
        <CommentsSection
          documentId={shareData.document.id}
          shareLinkId={shareData.id}
          permission={shareData.permission}
        />
      )}

      {/* Premium Viral CTA Section */}
      <motion.div 
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-50px" }}
        variants={fadeInUp}
        transition={{ duration: 0.6 }}
        className="border-t border-border/40 bg-gradient-to-b from-white via-white to-indigo-50/50"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          {/* Main CTA Card */}
          <motion.div 
            variants={scaleIn}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary via-indigo-600 to-indigo-700 shadow-2xl shadow-primary/30"
          >
            {/* Decorative elements */}
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxjaXJjbGUgZmlsbD0icmdiYSgyNTUsMjU1LDI1NSwwLjA1KSIgY3g9IjIwIiBjeT0iMjAiIHI9IjIiLz48L2c+PC9zdmc+')] opacity-50" />
            <motion.div 
              animate={{ 
                scale: [1, 1.1, 1],
                opacity: [0.1, 0.15, 0.1]
              }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" 
            />
            <motion.div 
              animate={{ 
                scale: [1, 1.15, 1],
                opacity: [0.15, 0.2, 0.15]
              }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              className="absolute bottom-0 left-0 w-64 h-64 bg-indigo-400/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" 
            />
            
            <div className="relative px-6 sm:px-10 py-8 sm:py-10">
              <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
                {/* Content */}
                <motion.div 
                  variants={staggerContainer}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  className="flex-1 text-center lg:text-left"
                >
                  <motion.div 
                    variants={fadeInUp}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 mb-5"
                  >
                    <motion.div
                      animate={{ rotate: [0, 15, -15, 0] }}
                      transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                    >
                      <Sparkles className="w-4 h-4 text-yellow-300" />
                    </motion.div>
                    <span className="text-sm font-semibold text-white">Gestion intelligente de documents</span>
                  </motion.div>
                  
                  <motion.h2 
                    variants={fadeInUp}
                    className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4 leading-tight"
                  >
                    Vous aimez ce que vous voyez ?
                    <br />
                    <span className="text-indigo-200">Essayez EasyFinder gratuitement</span>
                  </motion.h2>
                  
                  <motion.p 
                    variants={fadeInUp}
                    className="text-base sm:text-lg text-indigo-100 max-w-xl mb-6"
                  >
                    Organisez, recherchez et partagez vos documents en quelques secondes grâce à l'intelligence artificielle.
                  </motion.p>
                  
                  {/* Features Grid */}
                  <motion.div 
                    variants={staggerContainer}
                    className="grid grid-cols-2 gap-3 max-w-md mx-auto lg:mx-0 mb-6"
                  >
                    <motion.div variants={featureItem} className="flex items-center gap-2.5 text-sm text-white/90">
                      <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
                        <Search className="w-4 h-4 text-white" />
                      </div>
                      <span>Recherche IA</span>
                    </motion.div>
                    <motion.div variants={featureItem} className="flex items-center gap-2.5 text-sm text-white/90">
                      <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
                        <FolderOpen className="w-4 h-4 text-white" />
                      </div>
                      <span>Classement auto</span>
                    </motion.div>
                    <motion.div variants={featureItem} className="flex items-center gap-2.5 text-sm text-white/90">
                      <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
                        <ScanLine className="w-4 h-4 text-white" />
                      </div>
                      <span>Scanner PDF</span>
                    </motion.div>
                    <motion.div variants={featureItem} className="flex items-center gap-2.5 text-sm text-white/90">
                      <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
                        <Share2 className="w-4 h-4 text-white" />
                      </div>
                      <span>Partage sécurisé</span>
                    </motion.div>
                  </motion.div>
                </motion.div>
                
                {/* CTA Column */}
                <motion.div 
                  variants={slideInFromRight}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className="shrink-0 flex flex-col items-center gap-4"
                >
                  <motion.div
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Button 
                      size="lg" 
                      onClick={() => navigate("/auth")}
                      className="h-14 sm:h-16 px-8 sm:px-10 text-base sm:text-lg font-bold rounded-2xl bg-white text-primary hover:bg-indigo-50 shadow-xl hover:shadow-2xl transition-all duration-300 group"
                    >
                      Créer mon compte gratuit
                      <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </motion.div>
                  
                  <motion.div 
                    variants={fadeIn}
                    className="flex items-center gap-4 text-xs text-indigo-200"
                  >
                    <div className="flex items-center gap-1.5">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      <span>100% gratuit</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      <span>Sans carte bancaire</span>
                    </div>
                  </motion.div>
                </motion.div>
              </div>
            </div>
          </motion.div>

          {/* Trust Indicators */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="flex flex-wrap items-center justify-center gap-6 mt-8 text-sm text-muted-foreground"
          >
            <motion.div 
              whileHover={{ scale: 1.05 }}
              className="flex items-center gap-2"
            >
              <Shield className="w-4 h-4 text-primary" />
              <span>Données sécurisées</span>
            </motion.div>
            <motion.div 
              whileHover={{ scale: 1.05 }}
              className="flex items-center gap-2"
            >
              <Zap className="w-4 h-4 text-primary" />
              <span>Configuration en 2 min</span>
            </motion.div>
            <motion.div 
              whileHover={{ scale: 1.05 }}
              className="flex items-center gap-2"
            >
              <CheckCircle2 className="w-4 h-4 text-primary" />
              <span>Conforme RGPD</span>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>

      {/* Footer with Branding */}
      <motion.footer 
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.4 }}
        className="border-t border-border/40 bg-white py-6"
      >
        <div className="max-w-6xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <motion.button 
            onClick={() => navigate("/")} 
            className="hover:opacity-80 transition-opacity"
            whileHover={{ scale: 1.02 }}
          >
            <EasyFinderLogo size="small" />
          </motion.button>
          <p className="text-xs text-muted-foreground text-center">
            © {new Date().getFullYear()} EasyFinder. La gestion de documents intelligente.
          </p>
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate("/privacy")} 
              className="text-xs text-muted-foreground hover:text-primary transition-colors"
            >
              Confidentialité
            </button>
            <button 
              onClick={() => navigate("/terms")} 
              className="text-xs text-muted-foreground hover:text-primary transition-colors"
            >
              CGU
            </button>
          </div>
        </div>
      </motion.footer>
    </div>
  );
};

export default SharedDocument;
